import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { timesheetsApi } from "../api/timesheets";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import type { TimesheetStatus } from "../types";

// Timesheets submitted for periods up to and including this month are not required.
// The missing-timesheet warning is suppressed for any period ≤ this cutoff.
const MISSING_CHECK_CUTOFF = { year: 2026, month: 4 };

/** Primary-card statuses that count as filed — mirror of the backend's
 *  apps.accounts.contract_timeline.FILED_TIMESHEET_STATUSES. */
const FILED_TIMESHEET_STATUSES: TimesheetStatus[] = ["pending", "approved", "withdrawal_requested"];

function getPreviousMonthPeriod() {
  const now = new Date();
  const month = now.getMonth() === 0 ? 12 : now.getMonth();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return { year, month };
}

function isBeforeCutoff(year: number, month: number): boolean {
  return (
    year < MISSING_CHECK_CUTOFF.year ||
    (year === MISSING_CHECK_CUTOFF.year && month <= MISSING_CHECK_CUTOFF.month)
  );
}

export function useTimesheets(params?: Record<string, string>) {
  return useQuery({
    queryKey: ["timesheets", params],
    queryFn: () => timesheetsApi.list(params),
    staleTime: 0,
  });
}

export function useCreateTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => timesheetsApi.create(formData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
    },
  });
}

export function useApproveTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      timesheetsApi.approve(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
    },
  });
}

export function useRejectTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      timesheetsApi.reject(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
    },
  });
}

export function useApproveWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      timesheetsApi.approveWithdrawal(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
    },
  });
}

export function useRejectWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      timesheetsApi.rejectWithdrawal(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
    },
  });
}

/** CEO only: revoke an approval that was already granted. */
export function useRevokeApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      comment,
      confirmPayrollSettled,
    }: {
      id: string;
      comment: string;
      confirmPayrollSettled?: boolean;
    }) => timesheetsApi.revokeApproval(id, comment, confirmPayrollSettled ?? false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
      qc.invalidateQueries({ queryKey: ["timesheets-pending-count"] });
    },
  });
}

export function useDeleteTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => timesheetsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets"] });
      qc.invalidateQueries({ queryKey: ["timesheets-my-secondary-with-primary"] });
    },
  });
}

/** Count of pending timesheets waiting for director/accountant review. */
export function usePendingTimesheetsCount(enabled = true) {
  return useQuery({
    queryKey: ["timesheets-pending-count"],
    queryFn: () =>
      apiClient
        .get<{ data: { count: number } }>("/timesheets/pending_count/")
        .then((r) => r.data.data.count),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
}

/** Reviewer-only: submission ratio for a concrete (year, month). */
export function useSubmissionStats(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: ["timesheets", "submission-stats", year, month],
    queryFn: () => timesheetsApi.submissionStats(year, month),
    enabled,
    staleTime: 1000 * 30,
  });
}

/** Reviewer-only: employees with a saved PDF draft for (year, month). */
export function useDraftsOverview(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: ["timesheets", "drafts-overview", year, month],
    queryFn: () => timesheetsApi.draftsOverview(year, month),
    enabled,
    staleTime: 1000 * 30,
  });
}

/** Reviewer-only: employees without a submitted primary timesheet for (year, month). */
export function useTimesheetMissing(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: ["timesheets", "missing", year, month],
    queryFn: () => timesheetsApi.missing(year, month),
    enabled,
    staleTime: 1000 * 30,
  });
}

/** Employees this user may submit timesheets on behalf of. */
export function useProxyTargets(enabled = true) {
  return useQuery({
    queryKey: ["timesheets-proxy-targets"],
    queryFn: () => timesheetsApi.proxyTargets(),
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

/** Append-only audit log for one (year, month, contract).
 *  Employees can read their own (no employeeId); reviewers read others' (branch-scoped). */
export function useDraftHistory(
  year: number,
  month: number,
  secondaryContractId: string | null,
  employeeId?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      "timesheets",
      "draft-history",
      year,
      month,
      secondaryContractId,
      employeeId ?? null,
    ],
    queryFn: () =>
      timesheetsApi.draftHistory(year, month, secondaryContractId, employeeId),
    enabled,
    staleTime: 1000 * 15,
  });
}

/** Returns true when the current user hasn't submitted a timesheet for the previous month.
 *  Always returns false for periods up to and including MISSING_CHECK_CUTOFF,
 *  and for months that ended before the user's employment start date.
 *  Also suppressed when an admin has dismissed the alert for this user/period, and
 *  when the backend says no card is expected for that month at all (lock-status
 *  `required` — e.g. a month governed by B2B; the previous month of someone who
 *  just switched to B2B still is one).
 *  "Filed" = what lifts the lock and clears the sidebar badge (backend
 *  FILED_TIMESHEET_STATUSES): an own primary card that is pending, approved or
 *  awaiting withdrawal — a rejected or withdrawn one has to be filed again. */
export function useMissingTimesheet(enabled = true) {
  const { year, month } = getPreviousMonthPeriod();
  const cutoff = isBeforeCutoff(year, month);
  const userId = useAuthStore((s) => s.user?.id);
  const employmentStart = useAuthStore((s) => s.user?.employment_start_date);
  // ISO-string comparison avoids UTC-vs-local parsing pitfalls;
  // new Date(year, month, 0).getDate() = number of days in the 1-based `month`.
  const monthEndStr = `${year}-${String(month).padStart(2, "0")}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
  const beforeEmployment = !!employmentStart && employmentStart > monthEndStr;
  const { data: lock } = useTimesheetLockStatus(enabled && !cutoff && !beforeEmployment);
  const active = enabled && !cutoff && !beforeEmployment && !!lock?.required;

  const { data = [], isLoading } = useQuery({
    queryKey: ["timesheets", { year: String(year), month: String(month) }],
    queryFn: () => timesheetsApi.list({ year: String(year), month: String(month) }),
    staleTime: 1000 * 60 * 5,
    enabled: active,
  });
  const filed = data.some(
    (ts) =>
      ts.employee === userId &&
      !ts.secondary_contract &&
      FILED_TIMESHEET_STATUSES.includes(ts.status),
  );
  const { data: isDismissed = false, isLoading: isDismissedLoading } = useQuery({
    queryKey: ["timesheets", "my-missing-dismissal", year, month],
    queryFn: () => timesheetsApi.getMyMissingDismissal(year, month),
    staleTime: 1000 * 60 * 5,
    enabled: active && !isLoading && !filed,
  });

  return {
    isMissing: active && !isLoading && !isDismissedLoading && !filed && !isDismissed,
    year,
    month,
    /** The exact moment the card stops being on time (ISO, local 12:00 of the
     *  2nd working day) — the lock engages after it. */
    deadlineAt: lock?.deadline_at ?? null,
    /** False once the backend said no card is expected for that month (B2B month,
     *  cutoff, before employment, CEO); null while unknown. */
    required: lock ? lock.required : null,
    isLoading: isLoading || isDismissedLoading,
  };
}

/** Missing-timesheet system lock for the current user.
 *  When `locked`, the app is cut down to the Urlopy + Harmonogram views (see
 *  Sidebar + ProtectedLayout) until the overdue card is filed. Lives under the
 *  "timesheets" query key on purpose: every submit/withdraw mutation already
 *  invalidates that prefix, so the lock lifts as soon as a card goes in. */
export function useTimesheetLock() {
  const user = useAuthStore((s) => s.user);
  // Populations that never owe a card — don't even ask the server for them.
  // B2B is NOT one of them: the last UoP month before a switch to B2B still owes
  // its card, and the server knows which month is which (contract_timeline).
  const canBeLocked = !!user && user.role !== "contractor" && !user.is_ceo;

  const { data } = useTimesheetLockStatus(canBeLocked);

  return {
    isLocked: !!data?.locked,
    periodLabel: data?.period_label ?? "",
    year: data?.year,
    month: data?.month,
  };
}

/** Shared observer of /timesheets/lock-status/ (lock + "is a card expected"). */
function useTimesheetLockStatus(enabled: boolean) {
  return useQuery({
    queryKey: ["timesheets", "lock-status"],
    queryFn: () => timesheetsApi.lockStatus(),
    enabled,
    // The deadline passes at 12:00, mid-session for anyone already logged in —
    // poll slowly so the lock (and the banner) appears without a reload.
    refetchInterval: 1000 * 60 * 10,
  });
}

const TWO_MONTHS_MS = 2 * 30 * 24 * 60 * 60 * 1000;

/** Reviewer-only: returns a Map of employee_id → dismissal data and mutation helpers. */
export function useMissingDismissals(year: number, month: number, enabled = true) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["timesheets", "missing-dismissals", year, month],
    queryFn: () => timesheetsApi.getMissingDismissals(year, month),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
  const now = Date.now();
  const dismissedIds = new Set(data.map((d) => d.employee_id));
  /** employee_id → true if dismissal is recent enough (< 2 months) to show the Przywróć option */
  const recentDismissalIds = new Set(
    data
      .filter((d) => now - new Date(d.dismissed_at).getTime() < TWO_MONTHS_MS)
      .map((d) => d.employee_id),
  );

  const dismiss = useMutation({
    mutationFn: (employee_id: string) => timesheetsApi.dismissMissing(employee_id, year, month),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets", "missing-dismissals", year, month] });
      qc.invalidateQueries({ queryKey: ["timesheets", "my-missing-dismissal"] });
    },
  });

  const undismiss = useMutation({
    mutationFn: (employee_id: string) => timesheetsApi.undismissMissing(employee_id, year, month),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timesheets", "missing-dismissals", year, month] });
      qc.invalidateQueries({ queryKey: ["timesheets", "my-missing-dismissal"] });
    },
  });

  return { dismissedIds, recentDismissalIds, dismiss, undismiss };
}
